/**
 * Dashboard Page
 *
 * Main dashboard showing user workspaces and usage metrics
 */

import * as React from 'react';
import { Suspense } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Progress,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@forgenetwork/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useToast } from '@/shared/hooks/useToast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useWorkspaces,
  useRepositories,
  useUsageStats,
  useUsageLimits,
  type Workspace,
  type Repository,
  type UsageStat,
  type UsageLimit,
} from '@/hooks/useDashboard';
import {
  FolderGit2,
  FileText,
  Bot,
  BarChart3,
  Plus,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
} from 'lucide-react';
import { useCreateWorkspace } from '@/features/workspace/api';

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: () => (
    <ErrorBoundary>
      <Suspense fallback={<DashboardSkeleton />}>
        <DashboardPage />
      </Suspense>
    </ErrorBoundary>
  ),
});

function DashboardPage(): React.JSX.Element {
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Selected workspace state
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string | null>(null);

  // Workspace creation state
  const [showCreateWorkspace, setShowCreateWorkspace] = React.useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = React.useState('');
  const createWorkspaceMutation = useCreateWorkspace();

  // Fetch workspaces
  const { data: workspacesData, isLoading: workspacesLoading, error: workspacesError } = useWorkspaces();

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    try {
      const workspace = await createWorkspaceMutation.mutateAsync({ name: newWorkspaceName.trim() });
      toast({ title: 'Success', description: 'Workspace created successfully' });
      setNewWorkspaceName('');
      setShowCreateWorkspace(false);
      setSelectedWorkspaceId(workspace.id);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create workspace',
        variant: 'destructive',
      });
    }
  };

  // Ensure workspaces is always an array (defensive check)
  const workspaces = React.useMemo((): Workspace[] => {
    if (!workspacesData) return [];
    if (Array.isArray(workspacesData)) return workspacesData;
    // Handle case where API returns { workspaces: [...] }
    if (workspacesData && typeof workspacesData === 'object' && 'workspaces' in workspacesData) {
      const ws = (workspacesData as { workspaces?: unknown }).workspaces;
      return Array.isArray(ws) ? ws : [];
    }
    return [];
  }, [workspacesData]);

  // Auto-select first workspace
  React.useEffect(() => {
    const firstWorkspace = workspaces[0];
    if (firstWorkspace && !selectedWorkspaceId) {
      setSelectedWorkspaceId(firstWorkspace.id);
    }
  }, [workspaces, selectedWorkspaceId]);

  // Fetch data for selected workspace
  const { data: repositoriesData, isLoading: reposLoading } = useRepositories(selectedWorkspaceId);
  const { data: usageStatsData, isLoading: statsLoading } = useUsageStats(selectedWorkspaceId, 'month');
  const { data: usageLimitsData } = useUsageLimits(selectedWorkspaceId);

  // Ensure arrays (defensive checks)
  const repositories = React.useMemo((): Repository[] => {
    if (!repositoriesData) return [];
    if (Array.isArray(repositoriesData)) return repositoriesData;
    if (repositoriesData && typeof repositoriesData === 'object' && 'repositories' in repositoriesData) {
      const repos = (repositoriesData as { repositories?: unknown }).repositories;
      return Array.isArray(repos) ? repos : [];
    }
    return [];
  }, [repositoriesData]);

  const usageStats = React.useMemo((): UsageStat[] => {
    if (!usageStatsData) return [];
    if (Array.isArray(usageStatsData)) return usageStatsData;
    if (usageStatsData && typeof usageStatsData === 'object' && 'stats' in usageStatsData) {
      const stats = (usageStatsData as { stats?: unknown }).stats;
      return Array.isArray(stats) ? stats : [];
    }
    return [];
  }, [usageStatsData]);

  const usageLimits = React.useMemo((): UsageLimit[] => {
    if (!usageLimitsData) return [];
    if (Array.isArray(usageLimitsData)) return usageLimitsData;
    if (usageLimitsData && typeof usageLimitsData === 'object' && 'limits' in usageLimitsData) {
      const limits = (usageLimitsData as { limits?: unknown }).limits;
      return Array.isArray(limits) ? limits : [];
    }
    return [];
  }, [usageLimitsData]);

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: 'Success',
        description: 'You have been logged out.',
      });
      navigate({ to: '/login' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Logout failed',
        variant: 'destructive',
      });
    }
  };

  // Compute stats from usage data (with defensive checks)
  const prdCount = Array.isArray(usageStats) 
    ? usageStats.find((s) => s?.eventType === 'prd_generated')?.count ?? 0 
    : 0;
  const analysisCount = Array.isArray(usageStats)
    ? usageStats.find((s) => s?.eventType === 'analysis_completed')?.count ?? 0
    : 0;
  const aiSessionCount = Array.isArray(usageStats)
    ? usageStats.find((s) => s?.eventType === 'acf_session_created')?.count ?? 0
    : 0;

  const selectedWorkspace = Array.isArray(workspaces)
    ? workspaces.find((w) => w?.id === selectedWorkspaceId)
    : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name || 'User'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {workspaces.length > 0 && (
              <Select
                value={selectedWorkspaceId ?? undefined}
                onValueChange={setSelectedWorkspaceId}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((workspace: Workspace) => (
                    <SelectItem key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </div>
        </div>

        {/* Error State */}
        {workspacesError && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>Failed to load workspaces. Please try again.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {workspacesLoading && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-3 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Workspaces State */}
        {!workspacesLoading && workspaces.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4 text-center py-8">
                <FolderGit2 className="h-12 w-12 text-muted-foreground" />
                <div>
                  <h2 className="font-semibold text-lg">No Workspaces Yet</h2>
                  <p className="text-muted-foreground text-sm">
                    Create a workspace to start organizing your repositories
                  </p>
                </div>
                {showCreateWorkspace ? (
                  <form onSubmit={handleCreateWorkspace} className="flex gap-2 w-full max-w-sm">
                    <Input
                      placeholder="Workspace name"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      disabled={createWorkspaceMutation.isPending}
                      autoFocus
                    />
                    <Button type="submit" disabled={createWorkspaceMutation.isPending || !newWorkspaceName.trim()}>
                      {createWorkspaceMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Create'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setShowCreateWorkspace(false);
                        setNewWorkspaceName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <Button onClick={() => setShowCreateWorkspace(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workspace
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Content */}
        {selectedWorkspace && (
          <>
            {/* Stats Cards */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <StatsCard
                title="Repositories"
                value={repositories.length}
                description="In this workspace"
                icon={FolderGit2}
                isLoading={reposLoading}
              />
              <StatsCard
                title="PRDs Generated"
                value={prdCount}
                description="This month"
                icon={FileText}
                isLoading={statsLoading}
              />
              <StatsCard
                title="AI Sessions"
                value={aiSessionCount}
                description="This month"
                icon={Bot}
                isLoading={statsLoading}
              />
              <StatsCard
                title="Analyses Run"
                value={analysisCount}
                description="This month"
                icon={BarChart3}
                isLoading={statsLoading}
              />
            </div>

            {/* Usage Limits */}
            {usageLimits.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Usage Limits</CardTitle>
                  <CardDescription>
                    Your current plan usage for this month
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {usageLimits.map((limit: UsageLimit) => (
                      <UsageLimitBar key={limit.eventType} limit={limit} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main Content Grid */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Recent Repositories */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Recent Repositories</CardTitle>
                    <CardDescription>
                      Your latest uploaded repositories
                    </CardDescription>
                  </div>
                  {repositories.length > 0 && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link
                        to="/repositories"
                        search={{ workspaceId: selectedWorkspaceId }}
                      >
                        View All
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {reposLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded" />
                          <div className="flex-1">
                            <Skeleton className="h-4 w-32 mb-1" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : repositories.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                      <FolderGit2 className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        No repositories yet
                      </p>
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/repositories">
                          <Plus className="h-4 w-4 mr-1" />
                          Upload Repository
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {repositories.slice(0, 5).map((repo: Repository) => (
                        <RepositoryRow key={repo.id} repository={repo} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                  <CardDescription>Common tasks and features</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link to="/repositories">
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Repository
                    </Link>
                  </Button>
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link to="/repositories">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate PRD
                    </Link>
                  </Button>
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link to="/repositories">
                      <Bot className="h-4 w-4 mr-2" />
                      Start AI Session
                    </Link>
                  </Button>
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link to="/repositories">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Run Code Analysis
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Getting Started (if no repositories) */}
            {repositories.length === 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Getting Started</CardTitle>
                  <CardDescription>
                    Start analyzing your code and generating PRDs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <GettingStartedStep
                      step={1}
                      title="Upload Your Repository"
                      description="Upload a ZIP file of your codebase to get started"
                      completed={false}
                    />
                    <GettingStartedStep
                      step={2}
                      title="Analyze Features"
                      description="Our system will extract and analyze features from your code"
                      completed={false}
                    />
                    <GettingStartedStep
                      step={3}
                      title="Generate PRD"
                      description="Get a comprehensive Product Requirements Document for your project"
                      completed={false}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Dashboard Skeleton Loader
 */
function DashboardSkeleton(): React.JSX.Element {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Card Component
 */
interface StatsCardProps {
  title: string;
  value: number;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}

function StatsCard({ title, value, description, icon: Icon, isLoading }: StatsCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <>
            <Skeleton className="h-8 w-16 mb-1" />
            <Skeleton className="h-3 w-20" />
          </>
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Usage Limit Bar Component
 */
interface UsageLimitBarProps {
  limit: {
    eventType: string;
    count: number;
    limit: number;
    remaining: number;
    period: string;
  };
}

function UsageLimitBar({ limit }: UsageLimitBarProps) {
  const percentage = limit.limit === -1 ? 0 : (limit.count / limit.limit) * 100;
  const isUnlimited = limit.limit === -1;
  const isNearLimit = percentage >= 80;

  // Format event type for display
  const formatEventType = (eventType: string) => {
    return eventType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{formatEventType(limit.eventType)}</span>
        <span className="text-muted-foreground">
          {isUnlimited ? (
            <Badge variant="secondary">Unlimited</Badge>
          ) : (
            <>
              {limit.count} / {limit.limit}
            </>
          )}
        </span>
      </div>
      {!isUnlimited && (
        <Progress
          value={Math.min(percentage, 100)}
          className={isNearLimit ? '[&>div]:bg-amber-500' : ''}
        />
      )}
    </div>
  );
}

/**
 * Repository Row Component
 */
interface RepositoryRowProps {
  repository: {
    id: string;
    name: string;
    description: string | null;
    fileCount: number | null;
    lastAnalyzedAt: string | null;
    createdAt: string;
  };
}

function RepositoryRow({ repository }: RepositoryRowProps) {
  const hasAnalysis = !!repository.lastAnalyzedAt;

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
        <FolderGit2 className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{repository.name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {repository.fileCount !== null && (
            <span>{repository.fileCount} files</span>
          )}
          {hasAnalysis ? (
            <Badge variant="outline" className="text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Analyzed
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              Pending
            </Badge>
          )}
        </div>
      </div>
      <Button variant="ghost" size="sm" asChild>
        <Link to="/repositories/$repoId" params={{ repoId: repository.id }}>
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}

/**
 * Getting Started Step Component
 */
interface GettingStartedStepProps {
  step: number;
  title: string;
  description: string;
  completed: boolean;
}

function GettingStartedStep({ step, title, description, completed }: GettingStartedStepProps) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
          completed
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {completed ? <CheckCircle2 className="h-4 w-4" /> : step}
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
